import { Dayjs } from "dayjs";
import { csl } from "lobbie";
import { generateUUID, notify } from "src/utils";

import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import { ECSLSignatureRequestType, ESearchTableType } from "src/types/global_enums";
import { triggerSearchTableRefetch } from "../../searchTableRefetchSlice";
import { createDrawerSlice, IBaseDrawerState } from "../createDrawerSlice";

export interface IContainerEntryDrawerState extends IBaseDrawerState {
    show: boolean;
    medicationId?: number;
    ndcVariant?: csl.INdcVariantFullDetails;
    locationId?: number;
    signer?: csl.ISignatureRequestSigner;
    signerSignedDateTimeISO?: string;
    signerSignatureBlob?: string; // base64 encoded signature
    isSignatureRequestMode: boolean;
    signatureRequestData?: csl.ISignatureRequestData;
    isLoading: boolean;
    error: string | null;
    isSignatureSubmitted: boolean;
}

export const initialStateContainerEntryDrawer: IContainerEntryDrawerState = {
    id: "containerEntryDrawer",
    show: false,
    medicationId: undefined,
    ndcVariant: undefined,
    locationId: undefined,
    signer: undefined,
    signerSignedDateTimeISO: undefined,
    signerSignatureBlob: undefined,
    isSignatureRequestMode: false,
    signatureRequestData: undefined,
    isLoading: false,
    error: null,
    isSignatureSubmitted: false,
};

export const startSignatureRequestTask = createAsyncThunk<
    csl.ISignatureRequestData,
    number,
    { rejectValue: string }
>(
    "containerEntryDrawer/startSignatureRequestTask",
    async (signatureRequestId: number, { rejectWithValue }) => {
        try {
            const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/signature-request/${signatureRequestId}/related-container-logs`;
            const { data } = await axios.get<csl.ISignatureRequestData>(route, {
                withCredentials: true,
            });
            if (!data?.containerLogs?.length) {
                notify({
                    level: "error",
                    title: "Error",
                    message: "No container entries found for this signature request",
                });
                return rejectWithValue("No container entries found");
            }
            return data;
        } catch (error) {
            notify({
                level: "error",
                title: "Error",
                message: "Failed to load signature request details",
            });
            return rejectWithValue("Failed to load signature request details");
        }
    },
);

interface ContainerEntrySubmitPayload {
    dateAddedToInventory: Date;
    timeReceived: string;
    containers?: {
        containerNumber: string;
        lotNumber: string;
        containerSize: number;
        expirationDate: Date;
    }[];
}

export const submitContainerEntryWithSignatureTask = createAsyncThunk<
    void,
    ContainerEntrySubmitPayload,
    { state: { containerEntryDrawer: IContainerEntryDrawerState }; rejectValue: string }
>(
    "containerEntryDrawer/submitContainerEntryWithSignature",
    async (formData, { getState, dispatch, rejectWithValue }) => {
        const state = getState().containerEntryDrawer;
        const { ndcVariant, locationId, signer, signerSignatureBlob, signerSignedDateTimeISO } =
            state;
        if (!ndcVariant || !locationId || !signer) {
            return rejectWithValue("Missing required data");
        }
        try {
            const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container/enter`;
            const response = await axios.post<csl.IContainerEntryResponse>(
                route,
                {
                    ndcVariantId: ndcVariant.id,
                    locationId,
                    dateAddedToInventory: formData.dateAddedToInventory,
                    timeReceived: formData.timeReceived,
                    containers: formData.containers,
                    primarySignerId: signer.id,
                    isPrimarySignerSigning: true,
                    primarySignerSignedDateTimeISO: signerSignedDateTimeISO,
                },
                { withCredentials: true },
            );
            const { signatureRequests } = response.data;
            if (signerSignatureBlob && signerSignedDateTimeISO) {
                const primarySignatureRequests = signatureRequests.filter(
                    (sr) =>
                        sr.signatureType === ECSLSignatureRequestType.PRIMARY &&
                        sr.signer.id === signer.id,
                );
                if (primarySignatureRequests.length > 0) {
                    const route_two = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/signature-request/batch-sign`;
                    await axios.post(
                        route_two,
                        {
                            signatureRequests: primarySignatureRequests.map((sr) => ({
                                signatureRequestId: sr.id,
                                signatureBlob: signerSignatureBlob,
                                ipAddress: window.location.hostname,
                            })),
                        },
                        { withCredentials: true },
                    );
                }
            }
            dispatch(
                triggerSearchTableRefetch([
                    ESearchTableType.CSL_CURRENT_INVENTORY,
                    ESearchTableType.CSL_CONTAINER_LOGS,
                ]),
            );
        } catch (error: any) {
            return rejectWithValue(error?.message || "Failed to submit container entry");
        }
    },
);

export const submitSignatureRequestTask = createAsyncThunk<
    void,
    undefined,
    { state: { containerEntryDrawer: IContainerEntryDrawerState } }
>(
    "containerEntryDrawer/submitSignatureRequest",
    async (_, { dispatch, getState, rejectWithValue }) => {
        const state = getState().containerEntryDrawer;
        const { signatureRequestData, signerSignatureBlob } = state;
        if (!signatureRequestData?.signatureRequest || !signerSignatureBlob) {
            return rejectWithValue("Missing signature data");
        }
        try {
            const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/signature-request/batch-sign`;
            await axios.post(
                route,
                {
                    signatureRequests: [
                        {
                            signatureRequestId: signatureRequestData.signatureRequest.id,
                            signatureBlob: signerSignatureBlob,
                            ipAddress: window.location.hostname,
                        },
                    ],
                },
                { withCredentials: true },
            );
            dispatch(
                triggerSearchTableRefetch([
                    ESearchTableType.CSL_CURRENT_INVENTORY,
                    ESearchTableType.CSL_CONTAINER_LOGS,
                ]),
            );
        } catch (error: any) {
            return rejectWithValue(error?.message || "Failed to submit signature");
        }
    },
);

const containerEntryDrawerSlice = createDrawerSlice({
    name: "containerEntryDrawer",
    initialState: initialStateContainerEntryDrawer,
    reducers: {
        startContainerEntry: {
            prepare: () => {
                return { payload: { id: generateUUID() } };
            },
            reducer: (state, action: PayloadAction<{ id: string }>) => {
                return {
                    ...initialStateContainerEntryDrawer,
                    id: action.payload.id,
                    show: true,
                };
            },
        },
        hideContainerEntryDrawer: (state) => ({
            ...state,
            show: false,
        }),
        setContainerLocationId: (state, action: PayloadAction<number | undefined>) => {
            state.locationId = action.payload;
            // Reset medication and NDC variant when location changes
            state.medicationId = undefined;
            state.ndcVariant = undefined;
        },
        setContainerMedicationId: (state, action: PayloadAction<number | undefined>) => {
            state.medicationId = action.payload;
        },
        setContainerNdcVariant: (
            state,
            action: PayloadAction<csl.INdcVariantFullDetails | undefined>,
        ) => {
            state.ndcVariant = action.payload;
        },
        setContainerSigner: (
            state,
            action: PayloadAction<csl.ISignatureRequestSigner | undefined>,
        ) => {
            state.signer = action.payload;
            state.signerSignedDateTimeISO = undefined;
            state.signerSignatureBlob = undefined;
        },
        setSignerSignedDate: (state, action: PayloadAction<Dayjs | undefined>) => {
            state.signerSignedDateTimeISO = action.payload?.toISOString();
        },
        setSignerSignatureBlob: (state, action: PayloadAction<string | undefined>) => {
            state.signerSignatureBlob = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(submitSignatureRequestTask.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(submitSignatureRequestTask.fulfilled, (state) => {
                return { ...initialStateContainerEntryDrawer };
            })
            .addCase(submitSignatureRequestTask.rejected, (state, action) => {
                state.isLoading = false;
                state.error = "Failed to submit signatures";
            })
            // Submitting a new container entry
            .addCase(submitContainerEntryWithSignatureTask.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(submitContainerEntryWithSignatureTask.fulfilled, (state) => {
                return initialStateContainerEntryDrawer;
            })
            .addCase(submitContainerEntryWithSignatureTask.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to submit container entry";
            })
            // Opening up the Container Entry Drawer to sign an existing container entry
            .addCase(startSignatureRequestTask.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(startSignatureRequestTask.fulfilled, (state, action) => {
                const firstEntry = action.payload.containerLogs[0];
                state.isLoading = false;
                state.show = true;
                state.isSignatureRequestMode = true;
                state.locationId = firstEntry.medication.locationId;
                state.medicationId = firstEntry.medication.id;
                state.ndcVariant = firstEntry.ndcVariant;
                state.signer = action.payload.signatureRequest.signer;
                state.signatureRequestData = action.payload;
                state.signerSignedDateTimeISO = action.payload.signatureRequest.completedOnISO;
                if (action.payload.signatureRequest.signatureBlob) {
                    state.isSignatureSubmitted = true;
                }
                state.signerSignatureBlob = action.payload.signatureRequest.signatureBlob;
            })
            .addCase(startSignatureRequestTask.rejected, (state, action) => {
                state.isLoading = false;
                state.error = "Failed to load signature request";
            });
    },
});

export const {
    startContainerEntry,
    setContainerLocationId,
    setContainerMedicationId,
    setContainerNdcVariant,
    hideContainerEntryDrawer,
    setContainerSigner,
    setSignerSignedDate,
    setSignerSignatureBlob,
} = containerEntryDrawerSlice.actions;

export default containerEntryDrawerSlice.reducer;
